import { applyAction } from '../game/actions.js';
import type {
  Action,
  GameEvent,
  GameState,
  MapDefinition,
  PlayerId,
} from '../game/types.js';
import { validateAction } from '../game/validation.js';
import type {
  ConnectionListener,
  ConnectionStatus,
  DispatchResult,
  Store,
  StoreListener,
} from './Store.js';

// Backwards-compat re-exports so existing imports keep working.
export type { DispatchResult } from './Store.js';
export type GameStoreListener = StoreListener;

/**
 * Single-player store. Owns GameState in memory and runs the same
 * applyAction() the multiplayer server uses, so single-player and
 * multiplayer share identical rule semantics.
 *
 * The Store interface is shared with RemoteGameStore — GameScene
 * accepts either and behaves identically.
 */
export class GameStore implements Store {
  private state: GameState;
  private readonly map: MapDefinition;
  private readonly listeners = new Set<GameStoreListener>();

  constructor(initialState: GameState, map: MapDefinition) {
    this.state = initialState;
    this.map = map;
  }

  getState(): GameState {
    return this.state;
  }

  getMap(): MapDefinition {
    return this.map;
  }

  dispatch(action: Action): DispatchResult {
    const validation = validateAction(this.state, action, this.map);
    if (!validation.ok) {
      return { ok: false, reason: validation.reason };
    }
    const result = applyAction(this.state, action, this.map);
    this.state = result.state;
    this.notify(result.events);
    return { ok: true, state: result.state, events: result.events };
  }

  isMultiplayer(): boolean {
    return false;
  }

  isLocalTurn(): boolean {
    // Hot-seat: every human seat is "the local user." Local turn iff
    // the current player is human (AiDriver handles the AI seats).
    const current = this.state.players.find(
      (p) => p.id === this.state.turn.currentPlayer
    );
    return current?.kind === 'human';
  }

  isLocalPlayer(playerId: PlayerId): boolean {
    // Hot-seat semantics: every human seat is "you" since they all
    // share one device. AI seats are never local.
    const p = this.state.players.find((p) => p.id === playerId);
    return p?.kind === 'human';
  }

  subscribe(fn: StoreListener): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  getConnectionStatus(): ConnectionStatus {
    // No network channel exists for the local store, so by definition
    // we're never disconnected. Lets UI code call this unconditionally
    // without branching on isMultiplayer().
    return 'connected';
  }

  onConnectionChange(_fn: ConnectionListener): () => void {
    // Status never changes for a local store, so the listener will
    // never fire. Returning a no-op cleanup keeps the unsubscribe
    // contract identical to RemoteGameStore.
    return () => {};
  }

  destroy(): void {
    // No external resources to release in single-player. The interface
    // requires this method so GameScene can call it unconditionally on
    // SHUTDOWN regardless of which store kind it's holding.
  }

  getTurnExpiresAt(): string | null {
    // No async timer in hot-seat single-player — the existing 60-sec
    // in-game timer is a different mechanic (live SP visual) and
    // doesn't live on the wire. v0.9.9 Hourglass.
    return null;
  }

  private notify(events: readonly GameEvent[]): void {
    // Isolate listeners — a buggy subscriber shouldn't kill the others.
    for (const fn of this.listeners) {
      try {
        fn(this.state, events);
      } catch (e) {
        console.error('GameStore listener threw:', e);
      }
    }
  }
}
